/**
 * Inline "Completed forms" viewer for the clinician patient-detail page.
 *
 * Reads /api/form-assignments/patient/{patientId}, filters to completed
 * assignments, and renders a collapsible card per response. Each row lazily
 * fetches the actual answers on first expand via GET /form-responses/assignment/{id}.
 *
 * Structured (or digitalised-PDF) submissions land here as instantly readable
 * clinical data — no separate report screen, no PDF chasing.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, ChevronDown, ChevronUp, Loader2, FileText, ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/Skeleton";

const PRETTY_TYPE = {
  boolean: (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : v || "—"),
  scale: (v) => (v == null || v === "" ? "—" : `${v} / 10`),
  date: (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—"),
};

function formatAnswer(field, raw) {
  if (raw == null || raw === "") return "—";
  const fn = PRETTY_TYPE[field?.type];
  if (fn) return fn(raw);
  if (Array.isArray(raw)) return raw.join(", ");
  return String(raw);
}

function ResponseRow({ a, onOpen }) {
  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !response) {
      setLoading(true);
      try {
        const { data } = await api.get(`/form-responses/assignment/${a.id}`);
        setResponse(data);
        onOpen && onOpen(a);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load response");
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const form = a.form || {};
  const fields = form.fields || [];
  const submittedOn = a.completed_at ? new Date(a.completed_at).toLocaleString("en-GB") : "—";

  return (
    <div
      className="p-4 border-b transition-colors hover:bg-white/5"
      style={{ borderColor: "var(--fl-border)" }}
      data-testid={`response-row-${a.id}`}
    >
      <button
        onClick={toggle}
        className="w-full text-left flex items-center gap-3"
        aria-expanded={open}
        data-testid={`response-toggle-${a.id}`}
      >
        <FileText size={14} className="text-[color:var(--fl-orange)] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{form.title || a.form_title}</div>
          <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
            Submitted {submittedOn} · Assigned by {a.clinician_name || "—"}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-emerald-400 shrink-0">Completed</span>
        {open ? <ChevronUp size={14} className="text-zinc-500 shrink-0" /> : <ChevronDown size={14} className="text-zinc-500 shrink-0" />}
      </button>

      {open && (
        <div className="mt-4 pl-6 space-y-3" data-testid={`response-body-${a.id}`}>
          {loading && <Skeleton className="h-16 w-full rounded-md" />}
          {!loading && response && (
            <>
              {fields.length === 0 && (
                <div className="text-xs text-zinc-500">This form has no structured fields on record.</div>
              )}
              {fields.map((fld) => (
                <div key={fld.key} className="grid grid-cols-3 gap-3 items-start">
                  <div className="col-span-1">
                    <div className="text-xs uppercase tracking-wider" style={{ color: "var(--fl-muted-2)" }}>{fld.label}</div>
                    {fld.type && <div className="text-[10px] text-zinc-600 mt-0.5">{fld.type}</div>}
                  </div>
                  <div className="col-span-2 text-sm text-zinc-200 whitespace-pre-wrap break-words">
                    {formatAnswer(fld, response.answers?.[fld.key])}
                  </div>
                </div>
              ))}
              {/* Show any answers the form's field list has since dropped, so nothing is silently lost. */}
              {(() => {
                const known = new Set(fields.map((f) => f.key));
                const extras = Object.entries(response.answers || {}).filter(([k]) => !known.has(k));
                if (extras.length === 0) return null;
                return (
                  <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--fl-border)" }}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Extra answers (field removed from form)</div>
                    {extras.map(([k, v]) => (
                      <div key={k} className="grid grid-cols-3 gap-3">
                        <div className="col-span-1 text-xs text-zinc-500">{k}</div>
                        <div className="col-span-2 text-sm text-zinc-300 whitespace-pre-wrap">{formatAnswer(null, v)}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PatientFormResponses({ patientId }) {
  const [assignments, setAssignments] = useState(null); // null = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    setAssignments(null);
    api.get(`/form-assignments/patient/${patientId}`)
      .then((r) => setAssignments(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load form assignments"))
      .finally(() => setAssignments((prev) => (prev === null ? [] : prev)));
  }, [patientId]);

  const completed = (assignments || []).filter((a) => a.status === "completed");

  return (
    <div className="fl-card fl-card-elevated overflow-hidden" data-testid="patient-responses-card">
      <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--fl-border)" }}>
        <div>
          <div className="fl-label text-[color:var(--fl-orange)] flex items-center gap-2"><ClipboardCheck size={11} /> Completed forms</div>
          <div className="fl-heading text-lg mt-1">Patient responses</div>
        </div>
        <div className="text-xs" style={{ color: "var(--fl-muted-2)" }} data-testid="response-count">
          {assignments === null ? "" : `${completed.length} response${completed.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {assignments === null && (
        <div className="p-4 space-y-3" data-testid="responses-loading">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-4 rounded" />
              <div className="flex-1">
                <Skeleton className="h-3 w-40 rounded" />
                <Skeleton className="h-2 w-56 mt-2 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {assignments !== null && error && (
        <div className="p-6 text-sm text-red-400">{error}</div>
      )}

      {assignments !== null && !error && completed.length === 0 && (
        <div className="p-8 text-sm text-center flex flex-col items-center gap-2" style={{ color: "var(--fl-muted-2)" }} data-testid="responses-empty">
          <ClipboardCheck size={20} className="text-zinc-600" />
          No completed forms yet.
          <a href="/clinician/forms" className="inline-flex items-center gap-1 text-[color:var(--fl-orange)] hover:underline text-xs">
            <ExternalLink size={11} /> Assign one from the Forms library
          </a>
        </div>
      )}

      {assignments !== null && !error && completed.length > 0 && (
        <div>
          {completed.map((a) => <ResponseRow key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}
